/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import type React from 'react'
import { createContext, useContext, useMemo, useState } from 'react'

import ChevronRight from '@mui/icons-material/ChevronRight'
import HelpOutline from '@mui/icons-material/HelpOutline'
import {
  DialogContent,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
} from '@mui/material'

import Dialog from './Dialog'
import HoverMenu from './HoverMenu'
import { MenuItemEndDecoration } from './Menu'
import { bindFocus, bindHover, bindMenu, usePopupState } from './hooks'

import type { MenuItem as JBMenuItem } from './Menu'
import type { PopupState } from './hooks'
import type { PopoverOrigin, SvgIconProps } from '@mui/material'

const CascadingContext = createContext({
  parentPopupState: undefined,
  rootPopupState: undefined,
} as {
  parentPopupState: PopupState | undefined
  rootPopupState: PopupState | undefined
})

function HelpIconButton({ helpText }: { helpText: string }) {
  const [helpDialogOpen, setHelpDialogOpen] = useState(false)

  const handleHelpClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setHelpDialogOpen(true)
  }

  const handleClose = (event: React.MouseEvent | React.KeyboardEvent) => {
    // Prevent backdrop click from propagating to menu item
    event.stopPropagation()
    setHelpDialogOpen(false)
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleHelpClick}
        style={{ marginLeft: 4, padding: 4 }}
      >
        <HelpOutline fontSize="small" />
      </IconButton>
      <Dialog
        open={helpDialogOpen}
        onClose={handleClose}
        title="Help"
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <DialogContent>{helpText}</DialogContent>
      </Dialog>
    </>
  )
}

function HelpIconSpacer() {
  // Empty spacer that matches HelpIconButton dimensions for alignment
  return <div style={{ marginLeft: 4, padding: 4, width: 28, height: 28 }} />
}

function CascadingMenuItem({
  onClick,
  closeAfterItemClick,
  children,
  ...props
}: {
  closeAfterItemClick: boolean
  onClick?: (event: React.MouseEvent<HTMLLIElement>) => void
  disabled?: boolean
  children: React.ReactNode
}) {
  const { rootPopupState, parentPopupState } = useContext(CascadingContext)

  if (!rootPopupState) {
    throw new Error('must be used inside a CascadingMenu')
  }

  return (
    <MenuItem
      {...props}
      onClick={event => {
        if (closeAfterItemClick) {
          rootPopupState.close()
        }
        onClick?.(event)
      }}
      onMouseOver={() => {
        // Close any existing child submenu when hovering over a regular menu item
        // Note: This logic is duplicated in CascadingSubmenu for consistency
        if (parentPopupState?.childHandle) {
          parentPopupState.childHandle.close()
          parentPopupState.setChildHandle(undefined)
        }
      }}
    >
      {children}
    </MenuItem>
  )
}

function CascadingSubmenu({
  title,
  Icon,
  inset,
  ...props
}: {
  children: React.ReactNode
  title: React.ReactNode
  onMenuItemClick: Function
  Icon: React.ComponentType<SvgIconProps> | undefined
  inset: boolean
  menuItems: JBMenuItem[]
}) {
  const { parentPopupState } = useContext(CascadingContext)
  const popupState = usePopupState({
    parentPopupState,
  })

  const { onMouseOver: originalOnMouseOver, ...hoverProps } =
    bindHover(popupState)

  return (
    <>
      <MenuItem
        {...bindFocus(popupState)}
        {...hoverProps}
        onMouseOver={event => {
          // Close any existing sibling submenus before opening this one
          // Note: This logic is duplicated from CascadingMenuItem for consistency
          if (parentPopupState?.childHandle) {
            parentPopupState.childHandle.close()
            parentPopupState.setChildHandle(undefined)
          }
          // Call the original hover handler to open this submenu
          originalOnMouseOver(event)
        }}
      >
        {Icon ? (
          <ListItemIcon>
            <Icon />
          </ListItemIcon>
        ) : null}
        <ListItemText primary={title} inset={inset} />
        <ChevronRight />
      </MenuItem>
      <CascadingSubmenuHover
        {...props}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        popupState={popupState}
      />
    </>
  )
}

function CascadingSubmenuHover({
  popupState,
  onMenuItemClick,
  menuItems,
  classes,
  ...props
}: {
  classes?: Record<string, string>
  popupState: PopupState
  anchorOrigin: PopoverOrigin
  transformOrigin: PopoverOrigin
  onMenuItemClick: Function
  menuItems: JBMenuItem[]
}) {
  const { rootPopupState } = useContext(CascadingContext)
  const context = useMemo(
    () => ({
      rootPopupState: rootPopupState || popupState,
      parentPopupState: popupState,
    }),
    [rootPopupState, popupState],
  )

  return (
    <CascadingContext.Provider value={context}>
      {/* @ts-expect-error */}
      <HoverMenu {...props} {...bindMenu(popupState)} />
    </CascadingContext.Provider>
  )
}

function CascadingMenu({
  popupState,
  onMenuItemClick,
  menuItems,
  ...props
}: {
  popupState: PopupState
  onMenuItemClick: Function
  menuItems: JBMenuItem[]
}) {
  const { rootPopupState } = useContext(CascadingContext)
  const context = useMemo(
    () => ({
      rootPopupState: rootPopupState || popupState,
      parentPopupState: popupState,
    }),
    [rootPopupState, popupState],
  )

  return (
    <CascadingContext.Provider value={context}>
      {/* @ts-expect-error */}
      <Menu {...props} {...bindMenu(popupState)} />
    </CascadingContext.Provider>
  )
}

function EndDecoration({ item }: { item: JBMenuItem }) {
  if ('subMenu' in item) {
    return <MenuItemEndDecoration type="subMenu" />
  } else if (item.type === 'checkbox' || item.type === 'radio') {
    return (
      <MenuItemEndDecoration
        type={item.type}
        checked={item.checked}
        disabled={item.disabled}
      />
    )
  }
  return null
}

function CascadingMenuList({
  onMenuItemClick,
  closeAfterItemClick,
  menuItems,
  ...props
}: {
  menuItems: JBMenuItem[]
  closeAfterItemClick: boolean
  onMenuItemClick: Function
}) {
  const hasIcon = menuItems.some(m => 'icon' in m && m.icon)
  const hasCheckboxOrRadioWithHelp = menuItems.some(
    m =>
      (m.type === 'checkbox' || m.type === 'radio') &&
      'helpText' in m &&
      m.helpText,
  )

  return (
    <>
      {menuItems
        .toSorted((a, b) => (b.priority || 0) - (a.priority || 0))
        .map((item, idx) => {
          return 'subMenu' in item ? (
            <CascadingSubmenu
              key={`subMenu-${item.label}-${idx}`}
              title={item.label}
              Icon={item.icon}
              inset={hasIcon && !item.icon}
              onMenuItemClick={onMenuItemClick}
              menuItems={item.subMenu}
            >
              <CascadingMenuList
                {...props}
                closeAfterItemClick={closeAfterItemClick}
                onMenuItemClick={onMenuItemClick}
                menuItems={item.subMenu}
              />
            </CascadingSubmenu>
          ) : item.type === 'divider' ? (
            <Divider
              key={`divider-${JSON.stringify(item)}-${idx}`}
              component="li"
            />
          ) : item.type === 'subHeader' ? (
            <ListSubheader key={`subHeader-${item.label}-${idx}`}>
              {item.label}
            </ListSubheader>
          ) : (
            <CascadingMenuItem
              key={`${item.label}-${idx}`}
              closeAfterItemClick={closeAfterItemClick}
              onClick={
                'onClick' in item
                  ? event => {
                      onMenuItemClick(event, item.onClick)
                    }
                  : undefined
              }
              disabled={Boolean(item.disabled)}
            >
              {item.icon ? (
                <ListItemIcon>
                  <item.icon />
                </ListItemIcon>
              ) : null}{' '}
              <ListItemText
                primary={item.label}
                secondary={item.subLabel}
                inset={hasIcon && !item.icon}
              />
              <CascadingSpacer />
              <EndDecoration item={item} />
              {item.type === 'checkbox' || item.type === 'radio' ? (
                'helpText' in item && item.helpText ? (
                  <HelpIconButton helpText={item.helpText} />
                ) : hasCheckboxOrRadioWithHelp ? (
                  <HelpIconSpacer />
                ) : null
              ) : 'helpText' in item && item.helpText ? (
                <HelpIconButton helpText={item.helpText} />
              ) : null}
            </CascadingMenuItem>
          )
        })}
    </>
  )
}

function CascadingSpacer() {
  return <div style={{ flexGrow: 1, minWidth: 10 }} />
}

function CascadingMenuChildren(props: {
  onMenuItemClick: Function
  closeAfterItemClick?: boolean
  menuItems: JBMenuItem[]
  popupState: PopupState
}) {
  const { closeAfterItemClick = true, ...rest } = props
  return (
    <CascadingMenu {...rest}>
      <CascadingMenuList {...rest} closeAfterItemClick={closeAfterItemClick} />
    </CascadingMenu>
  )
}

export default CascadingMenuChildren
